import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { type ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { type ModalContentProps, ModalLayout } from '@/core/features/modal';
import { listProperties, type Property } from '@/api/istaff/property';
import { isInMykonosBounds } from '@/constants/map';
import MykonosMap from '@/components/MykonosMap';
import MapPropertyMarker, {
  toMapCoordinate,
} from '@/components/MykonosMap/MapPropertyMarker';
import MapPanner from '@/components/MykonosMap/MapPanner';
import PropertyMapTile from './PropertyMapTile';

type Props = ModalContentProps & ConnectedProps<typeof connector>;

const MapModal = ({ closeModal }: Props) => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Property | null>(null);

  useEffect(() => {
    listProperties({ perPage: 50, include: ['media'] })
      .then((res) => setProperties(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const mappable = properties.filter(
    (p) =>
      toMapCoordinate(p.latitude) !== null &&
      toMapCoordinate(p.longitude) !== null,
  );
  const inBounds = mappable.filter((p) => {
    const lat = toMapCoordinate(p.latitude);
    const lng = toMapCoordinate(p.longitude);

    return lat !== null && lng !== null && isInMykonosBounds(lat, lng);
  });

  const selectedLat = toMapCoordinate(selected?.latitude);
  const selectedLng = toMapCoordinate(selected?.longitude);

  return (
    <ModalLayout
      closeModal={closeModal}
      title={t('property_locations')}
      bodySx={{ p: 0, overflow: 'hidden', flex: 1 }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <MykonosMap
            gestureHandling="greedy"
            onClick={() => setSelected(null)}
          >
            {inBounds.map((p) => (
              <MapPropertyMarker
                key={p.id}
                lat={p.latitude!}
                lng={p.longitude!}
                selected={selected?.id === p.id}
                onClick={() => setSelected(p)}
                label={p.name ?? undefined}
              />
            ))}

            {selectedLat !== null && selectedLng !== null && (
              <MapPanner lat={selectedLat} lng={selectedLng} />
            )}

            {/* Desktop only: tile anchored above the selected marker */}
            {selected && selectedLat !== null && selectedLng !== null && (
              <AdvancedMarker
                position={{ lat: selectedLat, lng: selectedLng }}
                zIndex={25}
                clickable
                anchorLeft="-50%"
                anchorTop="calc(-100% - 52px)"
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 416 }}>
                  <PropertyMapTile
                    property={selected}
                    onClose={() => setSelected(null)}
                    onNavigate={closeModal}
                  />
                </Box>
              </AdvancedMarker>
            )}
          </MykonosMap>

          {/* Mobile only: centered overlay outside the map DOM */}
          {selected && (
            <Box
              sx={{
                display: { xs: 'block', sm: 'none' },
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'calc(100% - 24px)',
                maxWidth: 360,
                zIndex: 10,
              }}
            >
              <PropertyMapTile
                property={selected}
                onClose={() => setSelected(null)}
                onNavigate={closeModal}
              />
            </Box>
          )}
        </Box>
      )}
    </ModalLayout>
  );
};

const connector = connect(null, {});

export default connector(MapModal);
